import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, EyeIcon, HiddenFieldIcon, Inscription, Preloader } from '@common-fe/common-fe';

import Permissions from '@/common/permissions';
import { useHasAccess, useSnackbar } from '@/modules/core';
import useCopyToClipboard from '@/utils/hooks/useCopyToClipboard';

import { useGetBankAccountNumberQuery } from './useGetBankAccountNumber.query';

const REQUIRED_PERMISSIONS = [{ permission: Permissions.VIEW_ER_BANK_ACCOUNT_NUMBER }];

interface Props {
  accountId: string;
  last4Value: string;
  accountNumberLength: number;
}
export const BankAccountField: React.FC<Props> = ({
  last4Value,
  accountNumberLength,
  accountId,
}) => {
  const hasViewAccess = useHasAccess(REQUIRED_PERMISSIONS);
  const [accountVisible, setAccountVisible] = useState(false);
  const [accountNumber, setAccountNumber] = useState('');
  const { handleAddPermanentSnackbar, handleReset } = useSnackbar();
  const { getAccountNumber, isLoading } = useGetBankAccountNumberQuery();

  const isAccountAlreadyFullVisible = useMemo(
    () => last4Value?.length === accountNumberLength,
    [last4Value, accountNumberLength]
  );

  const copyToClipboardAccountNumber = useCopyToClipboard(accountNumber || '');
  
  const handleCopyToClipboardAccountNumber = useCallback(
    (e: React.MouseEvent<Element, MouseEvent>) => {
      e.stopPropagation();
      copyToClipboardAccountNumber();
      handleAddPermanentSnackbar({
        text: 'Account number is copied!',
        closeIcon: true,
        zIndex: 2002,
      });
      setTimeout(() => {
        handleReset();
      }, 2500);
    }, [copyToClipboardAccountNumber, handleAddPermanentSnackbar, handleReset],
  );

  const currentIcon = useMemo(() => {
    if (accountVisible) {
      return (

        <HiddenFieldIcon
          color="iconSecondary"
          style={{
            cursor: 'pointer',
          }}
        />

      );
    }
    return (
      <EyeIcon
        color="iconSecondary"
        style={{
          cursor: 'pointer',
        }}
      />
    );
  }, [accountVisible]);

  const valueBeforeLast4 = useMemo(() => {
    return '•'.repeat(accountNumberLength - 4);
  }, [accountNumberLength]);

  const currentAccountNumberValue = useMemo(() => {
    if (accountVisible) {
      return (
        <Box onClick={handleCopyToClipboardAccountNumber}>
          <Inscription
            lineHeight='20px'
            color='textBody'
            weight="normal"
            style={{
              textDecoration: 'underline',
            }}
          >
            {accountNumber}
          </Inscription>
        </Box>
      );
    }
    return (
      <>
        <Inscription
          lineHeight='20px'
          color='textBody'
          weight="normal"
        >
          {valueBeforeLast4}
        </Inscription>
        <Inscription
          lineHeight='20px'
          color='textBody'
          margin={{ left: 'spacing4' }}
          weight="normal"
        >
          {last4Value}
        </Inscription>
      </>
    );
  }, [accountNumber, accountVisible, handleCopyToClipboardAccountNumber, last4Value, valueBeforeLast4]);

  const handleOnIconClick = useCallback(async(e: React.SyntheticEvent<Element, Event>) => {
    e.preventDefault();
    e.stopPropagation();

    if (!accountNumber) {
      const response = await getAccountNumber(accountId);
      setAccountNumber(response.data.toString());
    }
    setAccountVisible(!accountVisible);
  }
  , [accountId, accountNumber, accountVisible, getAccountNumber]);

  useEffect(() => {
    if (isAccountAlreadyFullVisible) {
      setAccountNumber(last4Value);
      setAccountVisible(true);
    }
  }, [isAccountAlreadyFullVisible, last4Value]);

  useEffect(() => {
    setAccountVisible(false);
    setAccountNumber('');
  }, [last4Value]);

  return (
    <Box
      height="24px"
      width="100%"
      direction="row"
      align="center"
    > 
      <Box
        height="100%"
        width="fit-content"
        align="center"
        justify="center"
        direction="row"
      >
        {isLoading ? (
          <Preloader />
        ) : currentAccountNumberValue}
      </Box>
      {!isAccountAlreadyFullVisible && hasViewAccess && (
        <Box
          height="100%"
          width="fit-content"
          align="center"
          justify="center"
          margin={{ left: 'spacing8' }}
          onClick={handleOnIconClick}
        >
          {currentIcon}
        </Box>
      )}
    </Box>
  );
};
